import React from 'react';
import { motion } from 'framer-motion';

interface MetricProps {
  label: string;
  value: number;
  maxValue: number;
  color: string;
  icon: React.ReactNode;
  status?: 'healthy' | 'warning' | 'critical';
}

interface SystemHealthRingProps {
  metrics: MetricProps[];
  size?: number;
}

const SystemHealthRing: React.FC<SystemHealthRingProps> = ({
  metrics,
  size = 400,
}) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const ringRadius = size * 0.35;
  const ringWidth = size * 0.08;

  // Calculate positions for metrics around the ring
  const metricPositions = metrics.map((_, index) => {
    const angle = (index / metrics.length) * 2 * Math.PI - Math.PI / 2;
    const x = centerX + ringRadius * Math.cos(angle);
    const y = centerY + ringRadius * Math.sin(angle);
    return { x, y, angle };
  });

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0">
        <defs>
          {/* Gradients for each metric */}
          {metrics.map((metric, index) => (
            <linearGradient
              key={`gradient-${index}`}
              id={`gradient-${index}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={metric.color} stopOpacity="0.8" />
              <stop offset="100%" stopColor={metric.color} stopOpacity="0.3" />
            </linearGradient>
          ))}

          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background rings */}
        <circle
          cx={centerX}
          cy={centerY}
          r={ringRadius}
          fill="none"
          stroke="currentColor"
          strokeWidth={ringWidth}
          className="text-gray-200 dark:text-gray-800"
          opacity="0.2"
        />

        {/* Metric arcs */}
        {metrics.map((metric, index) => {
          const percentage = metric.value / metric.maxValue;
          const startAngle = (index / metrics.length) * 2 * Math.PI - Math.PI / 2;
          const endAngle = startAngle + (2 * Math.PI / metrics.length) * percentage;

          const startX = centerX + ringRadius * Math.cos(startAngle);
          const startY = centerY + ringRadius * Math.sin(startAngle);
          const endX = centerX + ringRadius * Math.cos(endAngle);
          const endY = centerY + ringRadius * Math.sin(endAngle);

          const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

          return (
            <motion.path
              key={index}
              d={`M ${startX} ${startY} A ${ringRadius} ${ringRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
              fill="none"
              stroke={`url(#gradient-${index})`}
              strokeWidth={ringWidth}
              strokeLinecap="round"
              filter="url(#glow)"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: 2,
                delay: index * 0.2,
                ease: "easeOut",
              }}
            />
          );
        })}

        {/* Center status indicator */}
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={size * 0.12}
          className="fill-current text-gray-100 dark:text-gray-900"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        />

        {/* Pulsing glow in center if any critical status */}
        {metrics.some(m => m.status === 'critical') && (
          <motion.circle
            cx={centerX}
            cy={centerY}
            r={size * 0.12}
            fill="rgba(239, 68, 68, 0.3)"
            animate={{
              r: [size * 0.12, size * 0.15, size * 0.12],
              opacity: [0.5, 0.2, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
            }}
          />
        )}
      </svg>

      {/* Metric labels and icons */}
      {metrics.map((metric, index) => {
        const pos = metricPositions[index];
        const percentage = (metric.value / metric.maxValue) * 100;

        return (
          <motion.div
            key={index}
            className="absolute"
            style={{
              left: pos.x,
              top: pos.y,
              transform: 'translate(-50%, -50%)',
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 min-w-[120px]">
              {/* Icon */}
              <div className="flex items-center justify-center mb-2">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${metric.color}20` }}
                >
                  <div style={{ color: metric.color }}>
                    {metric.icon}
                  </div>
                </div>
              </div>

              {/* Value */}
              <div className="text-center">
                <motion.div
                  className="text-2xl font-bold text-gray-900 dark:text-gray-100"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                >
                  {metric.value}
                </motion.div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {metric.label}
                </div>

                {/* Progress bar */}
                <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: metric.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 1, delay: 1 + index * 0.1 }}
                  />
                </div>
              </div>

              {/* Status indicator */}
              {metric.status && (
                <div className="flex items-center justify-center mt-2">
                  <motion.div
                    className={`w-2 h-2 rounded-full ${
                      metric.status === 'healthy' ? 'bg-green-500' :
                      metric.status === 'warning' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    animate={{
                      scale: metric.status === 'critical' ? [1, 1.3, 1] : 1,
                      opacity: metric.status === 'critical' ? [1, 0.5, 1] : 1,
                    }}
                    transition={{
                      duration: 2,
                      repeat: metric.status === 'critical' ? Infinity : 0,
                    }}
                  />
                </div>
              )}
            </div>
          </motion.div>
        );
      })}

      {/* Center status text */}
      <div
        className="absolute flex flex-col items-center justify-center"
        style={{
          left: centerX,
          top: centerY,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <motion.div
          className="text-4xl font-bold text-gray-900 dark:text-gray-100"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          {metrics.every(m => m.status === 'healthy') ? '✓' :
           metrics.some(m => m.status === 'critical') ? '!' : '⚠'}
        </motion.div>
        <motion.div
          className="text-xs text-gray-600 dark:text-gray-400 mt-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          System
        </motion.div>
      </div>
    </div>
  );
};

export default SystemHealthRing;
